import React, { useEffect, useMemo } from "react";
import {
  Box,
  Group,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import moment from "moment";
import { ClientServicePackage } from "../../../types/packages";
import { BookedService } from "../../../types/transactions";
import {
  buildCoveredLines,
  capReasonTooltip,
  isPackageApplicable,
  remainingBalanceTooltipItems,
  sharedBalanceAwareMax,
  ServiceBasisRef,
} from "../../../utils/packageRedemptionUtils";
import { formatQty, unitLabelForBasis } from "../../../utils/quantityUtils";
import InlineQuantityStepper from "../../common/InlineQuantityStepper";
import RadioRingRow from "./RadioRingRow";
import styles from "./CheckoutPanel.module.css";

interface PackageRevealProps {
  /** All of the client's non-expired packages. */
  packages: ClientServicePackage[];
  bookedServices: BookedService[];
  /** Salon services (for per-basis unit labels + steps). */
  salonServices: ServiceBasisRef[];
  /** Business timezone for expiration display. */
  timezone?: string;
  selectedPackageId: string | null;
  onSelectPackage: (id: string) => void;
  /** Redeem amount per covered line, keyed by bookedServiceId. */
  packageRedemptions: Record<string, number>;
  onSetRedemption: (bookedServiceId: string, qty: number) => void;
  onSetRedemptions: (redemptions: Record<string, number>) => void;
}

/**
 * Package reveal: every non-expired package as a radio ring row with an info
 * tooltip listing the full remaining balance and the expiration date. Selecting
 * an applicable package reveals per-service +/- steppers (reusing the details
 * screen's InlineQuantityStepper) so multiple banked services can be redeemed
 * from ONE package at once; an inapplicable package shows a "nothing eligible"
 * note. Interactive controls live OUTSIDE the radio row (a button) to keep the
 * markup valid.
 */
export default function PackageReveal({
  packages,
  bookedServices,
  salonServices,
  timezone,
  selectedPackageId,
  onSelectPackage,
  packageRedemptions,
  onSetRedemption,
  onSetRedemptions,
}: PackageRevealProps) {
  const selectedPackage = packages.find((pkg) => pkg.id === selectedPackageId);

  const coveredLines = useMemo(
    () => buildCoveredLines(selectedPackage, bookedServices),
    [selectedPackage, bookedServices],
  );

  const basisById = useMemo(
    () => new Map(salonServices.map((svc) => [svc.id, svc.price_basis])),
    [salonServices],
  );

  // Soonest expiration first so staff burn the closest-to-expiring package
  // before the rest; open-ended ("No expiration") packages sort to the bottom.
  const sortedPackages = useMemo(
    () =>
      [...packages].sort((a, b) => {
        if (!a.expires_at) return 1;
        if (!b.expires_at) return -1;
        return Date.parse(a.expires_at) - Date.parse(b.expires_at);
      }),
    [packages],
  );

  // Greedy allocation of each covered line's max, respecting the shared package
  // balance across sibling lines of the same service. Used for the on-select
  // default and the "Redeem max" link.
  const buildMaxRedemptions = useMemo(
    () => () => {
      const next: Record<string, number> = {};
      coveredLines.forEach((line) => {
        next[line.bookedServiceId] = sharedBalanceAwareMax(
          line,
          next,
          coveredLines,
        );
      });
      return next;
    },
    [coveredLines],
  );

  // Auto-select the lone package so redemption needs zero extra clicks.
  useEffect(() => {
    if (!selectedPackageId && packages.length === 1) {
      onSelectPackage(packages[0].id);
    }
  }, [selectedPackageId, packages, onSelectPackage]);

  // Default each covered line to its max when a package is freshly selected
  // (no amounts entered yet). Explicit zeros are preserved (keys present).
  useEffect(() => {
    if (!selectedPackage || coveredLines.length === 0) return;
    const hasEntry = coveredLines.some(
      (line) => packageRedemptions[line.bookedServiceId] !== undefined,
    );
    if (hasEntry) return;
    onSetRedemptions(buildMaxRedemptions());
  }, [
    selectedPackage,
    coveredLines,
    packageRedemptions,
    onSetRedemptions,
    buildMaxRedemptions,
  ]);

  const formatExpiration = (expiresAt: string | null): string => {
    if (!expiresAt) return "No expiration";
    return `Expires ${moment
      .utc(expiresAt)
      .tz(timezone || "America/New_York")
      .format("MMM D, YYYY")}`;
  };

  const renderInfoTooltip = (pkg: ClientServicePackage) => {
    const items = remainingBalanceTooltipItems(pkg, salonServices);
    return (
      <Tooltip
        multiline
        w={240}
        withArrow
        position="top"
        events={{ hover: true, focus: true, touch: true }}
        classNames={{ tooltip: styles.balanceTooltip }}
        label={
          <Stack gap={2}>
            <Text
              size="xs"
              fw={700}
              tt="uppercase"
              c="inherit"
              style={{ opacity: 0.7 }}
            >
              Remaining balance
            </Text>
            {items.length > 0 ? (
              items.map((item, i) => (
                <Text key={i} size="sm" c="inherit">
                  {item}
                </Text>
              ))
            ) : (
              <Text size="sm" c="inherit">
                No remaining balance
              </Text>
            )}
          </Stack>
        }
      >
        <Box
          component="span"
          className={styles.packageInfoIcon}
          aria-label="View remaining balance"
        >
          <IconInfoCircle size={15} />
        </Box>
      </Tooltip>
    );
  };

  const renderRedeemPanel = () => (
    <Box className={styles.redeemPanel}>
      <Box className={styles.redeemPanelHeader}>
        <Box component="span" className={styles.redeemPanelTitle}>
          Amount to redeem
        </Box>
        <UnstyledButton
          type="button"
          className={`${styles.inlineLink} ${styles.redeemMaxLink}`}
          onClick={() => onSetRedemptions(buildMaxRedemptions())}
        >
          Redeem max
        </UnstyledButton>
      </Box>

      {coveredLines.map((line) => {
        const basis = basisById.get(line.salonServiceId);
        const unit = unitLabelForBasis(basis, 2);
        const max = sharedBalanceAwareMax(
          line,
          packageRedemptions,
          coveredLines,
        );
        return (
          <Box key={line.bookedServiceId} className={styles.redeemRow}>
            <Box className={styles.redeemRowLabel}>
              <Text component="span" fw={600} size="sm">
                {line.serviceName}
              </Text>{" "}
              <Text component="span" size="sm" c="dimmed">
                ({formatQty(line.quantityRemaining)} remaining)
              </Text>
            </Box>
            <InlineQuantityStepper
              value={Number(packageRedemptions[line.bookedServiceId]) || 0}
              onChange={(v) => onSetRedemption(line.bookedServiceId, v)}
              min={0}
              max={max}
              step={basis === "per_syringe" ? 0.5 : 1}
              decimalScale={2}
              unitLabel={unit || undefined}
              maxReachedTooltip={capReasonTooltip(
                line,
                packageRedemptions,
                coveredLines,
              )}
              ariaLabel={`${line.serviceName} quantity`}
            />
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box
      className={`${styles.reveal} ${styles.packageReveal}`}
      role="radiogroup"
      aria-label="Packages"
    >
      {sortedPackages.map((pkg) => {
        const applicable = isPackageApplicable(pkg, bookedServices);
        const isSelected = selectedPackageId === pkg.id;
        return (
          <React.Fragment key={pkg.id}>
            <RadioRingRow
              selected={isSelected}
              onSelect={() => onSelectPackage(pkg.id)}
              title={
                <Group gap={6} wrap="nowrap" align="center">
                  <Text component="span" fw={600} size="sm">
                    {pkg.name}
                  </Text>
                  {renderInfoTooltip(pkg)}
                </Group>
              }
              caption={formatExpiration(pkg.expires_at)}
            />
            {isSelected &&
              (applicable ? (
                renderRedeemPanel()
              ) : (
                <Box className={styles.emptyNote}>
                  Nothing on this ticket is eligible for this package.
                </Box>
              ))}
          </React.Fragment>
        );
      })}
    </Box>
  );
}
